import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, TextField, InputAdornment, Checkbox, Chip, Avatar, Button,
  IconButton, Tooltip, CircularProgress, Snackbar, Alert, alpha,
  Dialog, DialogContent, Select, MenuItem, FormControl,
} from '@mui/material';
import {
  Search as SearchIcon, CalendarMonth as CalIcon, DeleteOutline as DeleteIcon,
  Send as SendIcon, Person as PersonIcon, EditCalendar as RescheduleIcon,
  Close as CloseIcon, Replay as ReplayIcon, OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { designTokens as t } from '../theme';
import { getToken } from '../api';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const metaOf = (s) => (s && s.Data && s.Data.meta) || {};

const STATUS_META = {
  scheduled: { label: 'Scheduled', color: '#f43f5e' },
  published: { label: 'Published', color: '#10b981' },
  failed: { label: 'Failed', color: '#f87171' },
};

function statusOf(p) {
  if (p.error || p.Error) return 'failed';
  if (p.published || p.Published) return 'published';
  return 'scheduled';
}
const getField = (p, ...keys) => { for (const k of keys) if (p[k] != null) return p[k]; return undefined; };

function fmtDate(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: '2-digit' }) + ' at ' +
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function relTime(iso) {
  const d = new Date(iso); const now = new Date();
  const diff = (d - now) / 1000;
  if (isNaN(d)) return null;
  if (diff <= 0) return null;
  if (diff < 3600) return `in ${Math.round(diff / 60)}m`;
  if (diff < 86400) return `in ${Math.floor(diff / 3600)}h`;
  return `in ${Math.floor(diff / 86400)}j`;
}

export default function ScheduledPosts() {
  const [posts, setPosts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('scheduled');
  const [dateRange, setDateRange] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState('all');
  const [accountSearch, setAccountSearch] = useState('');
  const [postSearch, setPostSearch] = useState('');
  const [selection, setSelection] = useState(new Set());
  const [toast, setToast] = useState({ open: false, msg: '', sev: 'success' });
  const [reschedule, setReschedule] = useState({ open: false, id: null, value: '', saving: false });
  const notify = (msg, sev = 'success') => setToast({ open: true, msg, sev });

  const fetchAll = useCallback(async () => {
    try {
      const [p, s, g] = await Promise.all([
        axios.get(`${API}/api/scheduled-posts`),
        axios.get(`${API}/api/sessions`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/profiles/config`).catch(() => ({ data: { profiles: {} } })),
      ]);
      setPosts(Array.isArray(p.data?.posts) ? p.data.posts : []);
      setSessions(Array.isArray(s.data) ? s.data : []);
      setGroups(g.data?.profiles || {});
    } catch (e) { notify('Erreur de chargement', 'error'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); const id = setInterval(fetchAll, 30000); return () => clearInterval(id); }, [fetchAll]);

  const sessionMap = useMemo(() => {
    const m = {};
    sessions.forEach((s) => { m[s.Name] = { username: metaOf(s).username || s.Name, avatar: metaOf(s).profile_pic_url }; });
    return m;
  }, [sessions]);

  const GROUP_COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#ec4899', '#10b981', '#8b5cf6', '#ef4444', '#14b8a6'];
  const groupList = useMemo(() => Object.entries(groups).map(([id, g], i) => ({
    id, name: g.name || id, color: g.color || GROUP_COLORS[i % GROUP_COLORS.length],
  })), [groups]);
  // Normalise une valeur de groupe (id OU nom) vers son nom canonique en minuscule.
  const canonGroup = useCallback((val) => {
    if (!val) return '';
    const s = String(val).toLowerCase();
    const g = groupList.find((x) => x.id.toLowerCase() === s || x.name.toLowerCase() === s);
    return g ? g.name.toLowerCase() : s;
  }, [groupList]);
  // compte -> groupe canonique
  const accountGroup = useCallback((name) => {
    const s = sessions.find((x) => x.Name === name);
    return s ? canonGroup(s.Profile || metaOf(s).profile) : '';
  }, [sessions, canonGroup]);

  // nb de posts par groupe (pour le compteur du sélecteur)
  const groupCounts = useMemo(() => {
    const m = {};
    posts.forEach((p) => { const g = accountGroup(getField(p, 'session', 'Session')); if (g) m[g] = (m[g] || 0) + 1; });
    return m;
  }, [posts, accountGroup]);

  const counts = useMemo(() => {
    const c = { scheduled: 0, published: 0, failed: 0, drafts: 0 };
    posts.forEach((p) => { c[statusOf(p)]++; });
    return c;
  }, [posts]);

  const accountCounts = useMemo(() => {
    const m = {};
    posts.forEach((p) => { const s = getField(p, 'session', 'Session'); if (s) m[s] = (m[s] || 0) + 1; });
    return m;
  }, [posts]);

  const inDateRange = useCallback((iso) => {
    if (dateRange === 'all') return true;
    const d = new Date(iso); const now = new Date();
    if (isNaN(d)) return false;
    if (dateRange === 'today') return d.toDateString() === now.toDateString();
    if (dateRange === 'week') { const w = new Date(now); w.setDate(now.getDate() + 7); return d >= now && d <= w; }
    if (dateRange === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    return true;
  }, [dateRange]);

  const filtered = useMemo(() => {
    return posts.filter((p) => {
      if (statusOf(p) !== statusFilter) return false;
      const session = getField(p, 'session', 'Session');
      if (groupFilter !== 'all' && accountGroup(session) !== canonGroup(groupFilter)) return false;
      if (accountFilter !== 'all' && session !== accountFilter) return false;
      if (!inDateRange(getField(p, 'scheduledTime', 'ScheduledTime'))) return false;
      if (postSearch.trim()) {
        const msg = (getField(p, 'message', 'Message') || '').toLowerCase();
        if (!msg.includes(postSearch.toLowerCase())) return false;
      }
      return true;
    }).sort((a, b) => {
      const ta = new Date(getField(a, 'scheduledTime', 'ScheduledTime'));
      const tb = new Date(getField(b, 'scheduledTime', 'ScheduledTime'));
      // Published / Failed : les plus récents en haut. Scheduled / Drafts : le prochain d'abord.
      return (statusFilter === 'published' || statusFilter === 'failed') ? tb - ta : ta - tb;
    });
  }, [posts, statusFilter, groupFilter, accountFilter, inDateRange, postSearch, accountGroup, canonGroup]);

  const visibleAccounts = useMemo(() => sessions.filter((s) => {
    if (groupFilter !== 'all' && canonGroup(s.Profile || metaOf(s).profile) !== canonGroup(groupFilter)) return false;
    if (!accountSearch.trim()) return true;
    const meta = metaOf(s);
    return `${s.Name} ${meta.username || ''}`.toLowerCase().includes(accountSearch.toLowerCase());
  }), [sessions, accountSearch, groupFilter, canonGroup]);

  // Changer de groupe réinitialise le compte sélectionné s'il n'est plus visible.
  const handleGroupChange = (val) => {
    setGroupFilter(val);
    if (val !== 'all' && accountFilter !== 'all' && accountGroup(accountFilter) !== canonGroup(val)) {
      setAccountFilter('all');
    }
  };

  const allSelected = filtered.length > 0 && filtered.every((p) => selection.has(getField(p, 'id', 'ID')));
  const toggleAll = () => setSelection(allSelected ? new Set() : new Set(filtered.map((p) => getField(p, 'id', 'ID'))));
  const toggleOne = (id) => setSelection((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleBulkDelete = async () => {
    if (!selection.size) return;
    if (!window.confirm(`Supprimer ${selection.size} post(s) programmé(s) ?`)) return;
    try {
      await axios.post(`${API}/api/scheduled-posts/bulk-delete`, { ids: [...selection] });
      notify(`${selection.size} post(s) supprimé(s)`);
      setSelection(new Set());
      fetchAll();
    } catch (e) { notify('Erreur suppression', 'error'); }
  };
  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/api/scheduled-posts/${id}`); notify('Post supprimé'); fetchAll(); }
    catch (e) { notify('Erreur', 'error'); }
  };
  const handlePublishNow = async (id) => {
    try { await axios.post(`${API}/api/scheduled-posts/${id}/publish-now`); notify('Publication lancée'); setTimeout(fetchAll, 1500); }
    catch (e) { notify(e.response?.data?.error || 'Erreur', 'error'); }
  };
  // ISO -> valeur d'input datetime-local (heure locale, sans secondes)
  const isoToLocalInput = (iso) => {
    const d = new Date(iso);
    if (isNaN(d)) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const openReschedule = (id, iso) => setReschedule({ open: true, id, value: isoToLocalInput(iso), saving: false });
  const closeReschedule = () => setReschedule((r) => ({ ...r, open: false }));
  const handleReschedule = async () => {
    if (!reschedule.id || !reschedule.value) return;
    const d = new Date(reschedule.value);
    if (isNaN(d)) { notify('Date invalide', 'error'); return; }
    setReschedule((r) => ({ ...r, saving: true }));
    try {
      await axios.post(`${API}/api/scheduled-posts/${reschedule.id}/reschedule`, { scheduledTime: d.toISOString() });
      notify('Post reprogrammé');
      closeReschedule();
      fetchAll();
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur', 'error');
      setReschedule((r) => ({ ...r, saving: false }));
    }
  };

  const STATUS_TABS = [['scheduled', 'Scheduled'], ['drafts', 'Drafts'], ['published', 'Published'], ['failed', 'Failed']];
  const RANGES = [['all', 'All'], ['today', 'Today'], ['week', 'Week'], ['month', 'Month']];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.02em' }}>Scheduled Posts</Typography>
        <Typography sx={{ fontSize: '0.88rem', color: t.muted }}>Gère tes posts programmés, publiés et en échec</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
        {/* ===== filters ===== */}
        <Box sx={{ width: { xs: '100%', lg: 280 }, flexShrink: 0, border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.card, p: 2 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', color: t.mutedFaint, textTransform: 'uppercase', mb: 1.5 }}>Filtres</Typography>

          <Typography sx={{ fontSize: '0.68rem', color: t.mutedFaint, mb: 0.75, fontWeight: 600 }}>STATUT</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: 2 }}>
            {STATUS_TABS.map(([key, label]) => {
              const active = statusFilter === key;
              const dot = STATUS_META[key]?.color || t.mutedFaint;
              return (
                <Box key={key} onClick={() => setStatusFilter(key)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.85, borderRadius: 1.5, cursor: 'pointer',
                    bgcolor: active ? alpha('#fff', 0.06) : t.background, border: `1px solid ${active ? t.border : 'transparent'}`,
                    '&:hover': { bgcolor: alpha('#fff', 0.05) } }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dot }} />
                  <Typography sx={{ flex: 1, fontSize: '0.78rem', fontWeight: active ? 600 : 500 }}>{label}</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: t.mutedFaint }}>{counts[key] || 0}</Typography>
                </Box>
              );
            })}
          </Box>

          <Typography sx={{ fontSize: '0.68rem', color: t.mutedFaint, mb: 0.75, fontWeight: 600 }}>PÉRIODE</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
            {RANGES.map(([key, label]) => (
              <Box key={key} onClick={() => setDateRange(key)}
                sx={{ px: 1.25, py: 0.5, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.78rem',
                  bgcolor: dateRange === key ? t.primary : t.background, color: dateRange === key ? '#fff' : t.muted, fontWeight: dateRange === key ? 600 : 500,
                  '&:hover': { bgcolor: dateRange === key ? t.primaryHover : alpha('#fff', 0.05) } }}>
                {label}
              </Box>
            ))}
          </Box>

          {groupList.length > 0 && (
            <>
              <Typography sx={{ fontSize: '0.68rem', color: t.mutedFaint, mb: 0.75, fontWeight: 600 }}>GROUPE</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <Select value={groupFilter} onChange={(e) => handleGroupChange(e.target.value)} sx={{ bgcolor: t.background }}
                  renderValue={(val) => {
                    if (val === 'all') return 'Tous les groupes';
                    const g = groupList.find((x) => canonGroup(x.name) === canonGroup(val));
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {g && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: g.color }} />}
                        <span>{g ? g.name : val}</span>
                      </Box>
                    );
                  }}>
                  <MenuItem value="all">
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography sx={{ flex: 1, fontSize: '0.85rem' }}>Tous les groupes</Typography>
                      <Typography sx={{ fontSize: '0.74rem', color: t.mutedFaint }}>{posts.length}</Typography>
                    </Box>
                  </MenuItem>
                  {groupList.map((g) => (
                    <MenuItem key={g.id} value={g.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: g.color }} />
                        <Typography sx={{ flex: 1, fontSize: '0.85rem' }}>{g.name}</Typography>
                        <Typography sx={{ fontSize: '0.74rem', color: t.mutedFaint }}>{groupCounts[canonGroup(g.name)] || 0}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          <Typography sx={{ fontSize: '0.68rem', color: t.mutedFaint, mb: 0.75, fontWeight: 600 }}>COMPTES</Typography>
          <TextField fullWidth size="small" placeholder="Rechercher…" value={accountSearch} onChange={(e) => setAccountSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }} sx={{ mb: 1, '& .MuiOutlinedInput-root': { bgcolor: t.background } }} />
          <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
            <Box onClick={() => setAccountFilter('all')}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, borderRadius: 1.5, cursor: 'pointer', mb: 0.25,
                bgcolor: accountFilter === 'all' ? alpha(t.primary, 0.12) : 'transparent', '&:hover': { bgcolor: alpha('#fff', 0.04) } }}>
              <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: alpha('#fff', 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PersonIcon sx={{ fontSize: 15, color: t.muted }} />
              </Box>
              <Typography sx={{ flex: 1, fontSize: '0.82rem', fontWeight: 600 }}>Tous les comptes</Typography>
              <Typography sx={{ fontSize: '0.74rem', color: t.mutedFaint }}>{groupFilter === 'all' ? posts.length : (groupCounts[canonGroup(groupFilter)] || 0)}</Typography>
            </Box>
            {visibleAccounts.map((s) => {
              const meta = metaOf(s);
              const handle = meta.username || s.Name;
              const active = accountFilter === s.Name;
              return (
                <Box key={s.Name} onClick={() => setAccountFilter(s.Name)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.6, borderRadius: 1.5, cursor: 'pointer',
                    bgcolor: active ? alpha(t.primary, 0.12) : 'transparent', '&:hover': { bgcolor: alpha('#fff', 0.04) } }}>
                  <Avatar src={meta.profile_pic_url || undefined} sx={{ width: 26, height: 26, fontSize: '0.72rem', bgcolor: alpha(t.primary, 0.2), color: '#c7d2fe' }}>{handle.charAt(0).toUpperCase()}</Avatar>
                  <Typography noWrap sx={{ flex: 1, fontSize: '0.8rem' }}>@{handle}</Typography>
                  <Chip label={accountCounts[s.Name] || 0} size="small" sx={{ height: 18, fontSize: '0.68rem', bgcolor: alpha('#fff', 0.06), color: t.muted }} />
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ===== list ===== */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <TextField fullWidth size="small" placeholder="Rechercher des posts…" value={postSearch} onChange={(e) => setPostSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }} sx={{ '& .MuiOutlinedInput-root': { bgcolor: t.card } }} />
            {filtered.length > 0 && (
              <Checkbox size="small" checked={allSelected} indeterminate={!allSelected && selection.size > 0} onChange={toggleAll} />
            )}
          </Box>

          {selection.size > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, px: 1.5, py: 1, borderRadius: 2, bgcolor: alpha('#ef4444', 0.08), border: `1px solid ${alpha('#ef4444', 0.25)}` }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{selection.size} sélectionné(s)</Typography>
              <Button size="small" color="error" startIcon={<DeleteIcon sx={{ fontSize: 18 }} />} onClick={handleBulkDelete}>Supprimer</Button>
              <Button size="small" color="inherit" sx={{ color: t.muted }} onClick={() => setSelection(new Set())}>Annuler</Button>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={26} /></Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: t.mutedFaint, border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.card }}>
              <CalIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
              <Typography>Aucun post {STATUS_TABS.find(([k]) => k === statusFilter)?.[1].toLowerCase()}.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {filtered.map((p) => {
                const id = getField(p, 'id', 'ID');
                const session = getField(p, 'session', 'Session');
                const msg = getField(p, 'message', 'Message') || '';
                const sched = getField(p, 'scheduledTime', 'ScheduledTime');
                const uploads = getField(p, 'uploadIds', 'UploadIds') || [];
                const err = getField(p, 'error', 'Error');
                const st = statusOf(p);
                const sm = STATUS_META[st];
                const acc = sessionMap[session] || { username: session };
                const rel = st === 'scheduled' ? relTime(sched) : null;
                const imgUrls = (Array.isArray(uploads) ? uploads : []).filter(Boolean)
                  .map((u) => `${API}/api/uploads/${encodeURIComponent(u)}/raw?token=${getToken()}`);
                return (
                  <Box key={id} sx={{ border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.card, p: 2,
                    ...(selection.has(id) ? { borderColor: alpha(t.primary, 0.5) } : {}) }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Checkbox size="small" checked={selection.has(id)} onChange={() => toggleOne(id)} sx={{ p: 0.5 }} />
                      <Chip label={sm.label} size="small" sx={{ height: 22, bgcolor: alpha(sm.color, 0.15), color: sm.color, border: `1px solid ${alpha(sm.color, 0.3)}`, fontWeight: 600 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: t.mutedFaint }}>
                        <CalIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: '0.78rem' }}>{fmtDate(sched)}</Typography>
                      </Box>
                      {rel && <Typography sx={{ fontSize: '0.78rem', color: '#fca5a5', fontWeight: 600 }}>{rel}</Typography>}
                      <Box sx={{ flex: 1 }} />
                      {acc.username && (
                        <Tooltip title={`Ouvrir @${acc.username} sur Threads`}>
                          <IconButton size="small" component="a" href={`https://www.threads.com/@${acc.username}`} target="_blank" rel="noopener noreferrer" sx={{ color: t.muted }}>
                            <OpenInNewIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {st === 'scheduled' && (
                        <Tooltip title="Publier maintenant"><IconButton size="small" onClick={() => handlePublishNow(id)} sx={{ color: t.muted }}><SendIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      )}
                      {st === 'failed' && (
                        <Tooltip title="Réessayer maintenant"><IconButton size="small" onClick={() => handlePublishNow(id)} sx={{ color: '#34d399' }}><ReplayIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      )}
                      {(st === 'scheduled' || st === 'failed') && (
                        <Tooltip title={st === 'failed' ? 'Reprogrammer (réessayer)' : 'Reprogrammer'}><IconButton size="small" onClick={() => openReschedule(id, sched)} sx={{ color: t.muted }}><RescheduleIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      )}
                      <Tooltip title="Supprimer"><IconButton size="small" onClick={() => handleDelete(id)} sx={{ color: '#f87171' }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                          <Avatar src={acc.avatar || undefined} sx={{ width: 28, height: 28, fontSize: '0.78rem', bgcolor: alpha(t.primary, 0.2), color: '#c7d2fe' }}>{(acc.username || '?').charAt(0).toUpperCase()}</Avatar>
                          <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: t.primary }}>@{acc.username}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.86rem', whiteSpace: 'pre-wrap', color: t.foreground, lineHeight: 1.5 }}>{msg}</Typography>
                        {err && <Typography sx={{ fontSize: '0.78rem', color: '#f87171', mt: 0.5 }}>⚠ {err}</Typography>}
                      </Box>
                      {imgUrls.length > 0 && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: imgUrls.length > 1 ? '1fr 1fr' : '1fr', gap: 0.75, flexShrink: 0 }}>
                          {imgUrls.slice(0, 4).map((u, i) => (
                            <Box key={i} sx={{ position: 'relative', width: 84, height: 84 }}>
                              <Box component="img" src={u} alt=""
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                sx={{ width: 84, height: 84, borderRadius: 2, objectFit: 'cover', border: `1px solid ${t.border}`, display: 'block' }} />
                              {i === 3 && imgUrls.length > 4 && (
                                <Box sx={{ position: 'absolute', inset: 0, borderRadius: 2, bgcolor: alpha('#000', 0.55), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>+{imgUrls.length - 4}</Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={reschedule.open} onClose={closeReschedule} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 3, backgroundImage: 'none' } }}>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, pt: 2.5, pb: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(t.primary, 0.14), color: t.primary }}>
              <RescheduleIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, letterSpacing: '-0.01em' }}>Reprogrammer</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: t.muted }}>Choisis une nouvelle date de publication</Typography>
            </Box>
            <IconButton size="small" onClick={closeReschedule} sx={{ color: t.muted }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
          <Box sx={{ px: 2.5, pb: 1 }}>
            <TextField
              type="datetime-local" fullWidth size="small"
              value={reschedule.value}
              onChange={(e) => setReschedule((r) => ({ ...r, value: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': { bgcolor: t.background },
                '& input': { colorScheme: 'dark', color: t.foreground, fontSize: '0.9rem' },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2.5, py: 2 }}>
            <Button onClick={closeReschedule} sx={{ textTransform: 'none', color: t.muted, fontWeight: 600 }}>Annuler</Button>
            <Button onClick={handleReschedule} disabled={reschedule.saving || !reschedule.value} variant="contained"
              startIcon={reschedule.saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <CalIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: t.primary, '&:hover': { bgcolor: t.primaryHover }, boxShadow: 'none' }}>
              Reprogrammer
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast.sev} variant="filled" onClose={() => setToast((s) => ({ ...s, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
